/**
 * 免登录审批 — the landing page for a link in an email.
 *
 * The one screen in the system reachable without an account, and therefore the
 * one that has to be careful. Three rules shape it:
 *
 *  1. **The token lives in the URL fragment and is wiped immediately.** Fragments
 *     never reach the access log or a Referer header; the query string does.
 *  2. **Preview is read-only.** Opening a link to check it is the right move, so
 *     nothing happens until the button is pressed.
 *  3. **The server decides everything.** Expiry, prior use, a revoked role and the
 *     step's own requirement are all checked server-side on both calls; this page
 *     only renders what it is told.
 *
 * There is no session here, so the two calls skip CSRF and carry no cookie. The
 * server still enforces same-origin, which is what stops a third-party page from
 * driving a visitor's browser into confirming a decision.
 */

import { useEffect, useState } from "react";

import { ApiError, publicApi } from "../../lib/api.ts";
import { Button, Plate, SectionTitle, TextArea } from "../../components/kit.tsx";

type Phase = "loading" | "preview" | "done" | "refused";

interface Preview {
  kind: string;
  action: "approve" | "reject";
  expiresAt: string;
  approval: { id: string; title: string; summary: string; status: string } | null;
}

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export function EmailApproval() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [token, setToken] = useState("");
  const [kind, setKind] = useState<"usage" | "application">("usage");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<{ title: string; body: string } | null>(null);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const rawToken = hash.get("token") ?? "";
    const rawKind = hash.get("kind") === "application" ? "application" : "usage";

    // Scrub the address bar before anything else. The token must not survive in
    // the URL where a screenshot or a shared link would carry it.
    window.history.replaceState(null, "", window.location.pathname);

    if (!TOKEN_PATTERN.test(rawToken)) {
      setMessage("审批链接无效：链接不完整、被修改过，或缺少审批码。请从原始邮件重新打开。");
      setPhase("refused");
      return;
    }

    setToken(rawToken);
    setKind(rawKind);
    void load(rawToken, rawKind);
  }, []);

  async function load(rawToken: string, rawKind: "usage" | "application") {
    try {
      const data = await publicApi.post<Preview>(
        "/api/public/email-approvals/preview",
        { token: rawToken, kind: rawKind }
      );
      setPreview(data);
      setPhase("preview");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "无法验证这个审批链接。");
      setPhase("refused");
    }
  }

  async function confirm() {
    setBusy(true);
    setMessage(null);
    try {
      const outcome = await publicApi.post<{ request: { id: string; status: string; title: string } }>(
        "/api/public/email-approvals/confirm",
        { token, kind, reviewNote: note }
      );
      // The link is spent. Drop it so a double-click cannot replay it.
      setToken("");
      setResult({
        title: outcome.request.status === "rejected" ? "申请已拒绝" : "申请已处理",
        body:
          outcome.request.status === "pending"
            ? `申请 ${outcome.request.id} 已记录你的同意，正在流转到下一级审批。结果邮件已发送给申请人。`
            : `申请 ${outcome.request.id} 已${outcome.request.status === "approved" ? "通过" : "拒绝"}。结果邮件已发送给申请人。`,
      });
      setPhase("done");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "提交失败，请稍后再试。");
    } finally {
      setBusy(false);
    }
  }

  const rejecting = preview?.action === "reject";

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col justify-center px-5 py-12">
      <div className="mb-6">
        <p className="silk-label text-[10px] text-silk-dim">科创中心 · 审批</p>
        <h1 className="mt-2 text-[20px] text-silk">邮件审批</h1>
      </div>

      {phase === "loading" ? (
        <Plate className="p-6">
          <SectionTitle>正在验证</SectionTitle>
          <p className="mt-3 text-[13px] text-silk-dim">正在核对审批码与当前权限…</p>
        </Plate>
      ) : null}

      {phase === "refused" ? (
        <Plate className="p-6">
          <SectionTitle>无法使用这个链接</SectionTitle>
          <p className="mt-3 text-[13px] leading-relaxed text-silk-dim">{message}</p>
          <p className="mt-4 text-[12px] text-silk-dim">
            链接可能已经过期（有效期 24 小时）、已被使用过，或你的权限在链接发出后发生了变化。
          </p>
        </Plate>
      ) : null}

      {phase === "preview" && preview ? (
        <Plate className="p-6">
          <div className="flex items-center gap-2">
            <span
              className={`silk-label border px-2 py-0.5 text-[10px] ${
                rejecting
                  ? "border-state-danger/60 text-state-danger"
                  : "border-state-live/60 text-state-live"
              }`}
            >
              {rejecting ? "拒绝" : "同意"}
            </span>
            <span className="data text-[11px] text-silk-dim">有效期至 {new Date(preview.expiresAt).toLocaleString("zh-CN")}</span>
          </div>

          <h2 className="mt-4 text-[16px] text-silk">
            确认{rejecting ? "拒绝" : "通过"}「{preview.approval?.title ?? "这条申请"}」
          </h2>

          <dl className="mt-4 flex flex-col gap-2 border-t border-line pt-4 text-[13px]">
            <Row label="申请单号" value={preview.approval?.id ?? "—"} />
            <Row label="摘要" value={preview.approval?.summary || "—"} />
            <Row label="当前状态" value={preview.approval?.status ?? "—"} />
          </dl>

          <p className="mt-4 text-[12px] leading-relaxed text-silk-dim">
            现在只是预览，<b>还没有做任何改动</b>。只有点下面的按钮才会真正执行。
          </p>

          <div className="mt-4">
            <label htmlFor="approval-note" className="silk-label block text-[10px] text-silk-dim">
              审批意见（可选）
            </label>
            <TextArea
              id="approval-note"
              rows={3}
              maxLength={500}
              className="mt-2"
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              placeholder="补充批准条件或拒绝原因，会写进结果邮件"
            />
          </div>

          {message ? <p className="mt-3 text-[12px] text-state-danger">{message}</p> : null}

          <div className="mt-5 flex items-center gap-3">
            <Button
              variant={rejecting ? "danger" : "primary"}
              disabled={busy}
              onClick={() => void confirm()}
            >
              {busy ? "处理中" : rejecting ? "确认拒绝" : "确认通过并执行"}
            </Button>
          </div>
        </Plate>
      ) : null}

      {phase === "done" && result ? (
        <Plate className="p-6">
          <SectionTitle>{result.title}</SectionTitle>
          <p className="mt-3 text-[13px] leading-relaxed text-silk-dim">{result.body}</p>
          <p className="mt-4 text-[12px] text-silk-dim">这个链接已经用过了，不能再次使用。</p>
          <a
            href="/sign-in"
            className="silk-label mt-5 inline-block border border-line px-3 py-1.5 text-[12px] text-silk hover:border-silk"
          >
            返回登录
          </a>
        </Plate>
      ) : null}
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-silk-dim">{label}</dt>
      <dd className="data text-right text-silk">{value}</dd>
    </div>
  );
}
